import { useCallback, useLayoutEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { LPP_COUNTRY_LANGUAGE_CODES } from "../data/regions";
import { ApplicationState } from "src/store";
import { PrivacyState } from "src/store/reducers/privacy-reducer";
import { IFRAME_ID } from "../constants/privacyPolicyConstants";
import {
  applyIframeTheme,
  setIframeHeight,
  normalizeIframeContent,
  injectBrNormalizationStyles,
} from "../utils/privacyPolicyUtils";

interface UsePrivacyPolicyReturn {
  hasLPP: boolean;
  iframeLoadedGlobal: () => void;
  iframeLoadedLocal: () => void;
}

/**
 * Custom hook for managing privacy policy display
 * Handles LPP detection, iframe load handlers, and responsive resize handling
 */
export const usePrivacyPolicy = (
  isDarkMode: boolean,
  containerRef?: React.RefObject<HTMLDivElement>
): UsePrivacyPolicyReturn => {
  const privacyState = useSelector<ApplicationState, PrivacyState>(
    (state) => state.privacy
  );
  const ppLanguage = privacyState.language;

  // Check if language-country combination has Local Privacy Policy
  const hasLPP = useMemo(() => {
    return LPP_COUNTRY_LANGUAGE_CODES.includes(ppLanguage);
  }, [ppLanguage]);

  // Create iframe load handler factory for independent iframe handling
  const createIframeLoadHandler = useCallback(
    (iframeId: string) => () => {
      const iframe = document.getElementById(
        iframeId
      ) as HTMLIFrameElement | null;
      if (!iframe?.contentWindow || !iframe.contentDocument) {
        return;
      }
      const doc = iframe.contentDocument;

      normalizeIframeContent(doc);
      applyIframeTheme(doc, isDarkMode);
      setIframeHeight(doc, iframe);
    },
    [isDarkMode]
  );

  const iframeLoadedGlobal = createIframeLoadHandler(IFRAME_ID);
  const iframeLoadedLocal = createIframeLoadHandler(`${IFRAME_ID}-local`);

  // Synchronously set up ResizeObserver before paint for accurate measurements
  useLayoutEffect(() => {
    if (!containerRef?.current) {
      return () => {};
    }

    const updateIframeHeights = () => {
      iframeLoadedGlobal();
      iframeLoadedLocal();
    };

    const resizeObserver = new ResizeObserver(updateIframeHeights);
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [iframeLoadedGlobal, iframeLoadedLocal, containerRef]);

  return {
    hasLPP,
    iframeLoadedGlobal,
    iframeLoadedLocal,
  };
};
